import { CarouselEvent } from "@/components/Carousel/Event"
import { Loading } from "@/components/Loading/SpinerLoading"
import { Meta } from "@/components/meta/Meta"
import { APP_CONFIG } from "@/config"
import { getPage } from "@/data/get-page-by-id"
import { getWebsiteSettings } from "@/data/get-website-settings"
import { getEventUrl, getImageUrl } from "@/data/helpers"
import { getNavigations } from "@/data/navigations"
import { useEventsQuery } from "@/gql/generated-types"
import { BreadcrumbWithCover } from "@/layout/BreadcrumbWithCover"
import { MainLayout } from "@/layout/MainLayout"
import { Box, Container, Flex, Grid, MantineTheme, Text, createStyles } from "@mantine/core"
import dayjs from "dayjs"
import { isEmpty } from "lodash"
import { GetStaticProps, InferGetStaticPropsType } from "next"
import { useTranslation } from "next-i18next"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import Link from "next/link"
import { useRouter } from "next/router"

const useStyles = createStyles((theme: MantineTheme) => ({
  events: {
    rowGap: "1rem",
  },
  eventItem: {
    "&:hover": {
      "& img": {
        transform: "scale(1.1)",
      },
    },
  },
  eventItemImage: {
    height: "220px",
    overflow: "hidden",
    borderRadius: 8,
    "& img": {
      transition: "all .5s",
      width: "100%",
      // height: "100%",
      borderRadius: 8,
      objectFit: "cover",
    },
    [theme.fn.largerThan("sm")]: {
      height: "280px",
    },
  },
  eventItemInfo: {
    padding: ".5rem 0 0 0",
  },
  title: {
    color: "#333",
    fontSize: "1.125rem",
    fontWeight: 600,
    padding: ".25rem 0",
    display: "block",
  },
}))

export default function Index(props: InferGetStaticPropsType<typeof getStaticProps>) {
  const { t } = useTranslation("common")
  const { locale } = useRouter()
  const page = props.page

  const { classes } = useStyles()

  const { data, loading } = useEventsQuery({
    variables: {
      locale,
      filters: { websites: { domain: { eq: APP_CONFIG.WEBSITE } } },
    },
    fetchPolicy: "network-only",
  })

  const events = data?.events?.data

  // if (!loading && events?.length == 0) return null

  return (
    <MainLayout navigations={props.navigations} settings={props.websiteSettings}>
      <>
        <Meta data={page?.attributes?.seo!} />
        <BreadcrumbWithCover
          cover={page?.attributes?.seo?.metaImage?.data?.attributes?.url}
          title={t("Events", "Events")}
          breadcrumbs={[{ title: t("Events", "Events"), url: "/events" }]}
        />

        <Container mt={60} mb={94}>
          <Flex p={0} m={0} direction={"column"}>
            <CarouselEvent hasViewAll={false} />
          </Flex>

          <Grid columns={12} gutter={"1.5rem"} className={classes.events}>
            <Loading isLoading={loading} isEmpty={isEmpty(events)} />
            {events?.map(e => {
              return (
                <Grid.Col span={12} sm={4} key={e.id} className={classes.eventItem}>
                  <Link href={getEventUrl(e.attributes?.name, e.id!)} className={classes.title}>
                    <Box className={classes.eventItemImage}>
                      <img
                        src={getImageUrl(
                          e.attributes?.cover.data?.attributes?.url,
                          e.attributes?.cover.data?.attributes?.provider,
                        )}
                        alt="Event"
                      />
                    </Box>
                  </Link>
                  <Box className={classes.eventItemInfo}>
                    <Flex justify={"space-between"} align={"center"}>
                      <Text color="green" transform="uppercase" fz={"sm"} fw={700}>
                        {t("Event", "Event")}
                      </Text>
                      <Text c={"#555"} italic fz={"sm"}>
                        {dayjs(new Date(e.attributes?.date)).format("DD/MM/YYYY")}
                      </Text>
                    </Flex>
                    <Link href={getEventUrl(e.attributes?.name, e.id!)} className={classes.title}>
                      {e.attributes?.name}
                    </Link>
                    <Text fz={{ base: ".875rem", sm: "1rem" }} lh={1.3}>
                      {e.attributes?.shortDescription}
                    </Text>
                  </Box>
                </Grid.Col>
              )
            })}
          </Grid>
        </Container>
      </>
    </MainLayout>
  )
}

interface Props {
  navigations: Awaited<ReturnType<typeof getNavigations>>
  page: Awaited<ReturnType<typeof getPage>>
  websiteSettings: Awaited<any>
}

export const getStaticProps: GetStaticProps<Props> = async ({ locale = "en" }) => {
  const navigations = await getNavigations(locale)
  const page = await getPage({ id: APP_CONFIG.NEWS_PAGE_ID.find(e => e.locale === locale)!.pageId, locale })
  if (!page) {
    return {
      notFound: true,
    }
  }
  const websiteSettings = await getWebsiteSettings()
  return {
    props: {
      ...(await serverSideTranslations(locale ?? "en", ["common"])),
      navigations,
      page,
      websiteSettings,
    },
    revalidate: 10,
  }
}
